import { ApiDashboardElement } from '@highimpact-frontend/api-types';
import { SelectComponent, TextInput } from '@highimpact/dashboard-ui';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import type { MigratedPlotFormProps } from '../../../../forms/formHandlerShared';
import {
  getName,
  isValid,
  setName,
} from '../../../../state/dashboard-element-operations';

export interface WidgetTypeInitiatorFormProps extends MigratedPlotFormProps {
  typeSelectorLabel: string;
  options: { id: string; label: string }[];
  getSelectedTypeId: (config: ApiDashboardElement) => string;
  onTypeChange: (
    typeId: string,
    config: ApiDashboardElement
  ) => ApiDashboardElement;
}

function WidgetTypeInitiatorForm({
  config,
  onSubmit,
  onChange,
  typeSelectorLabel,
  options,
  getSelectedTypeId,
  onTypeChange,
}: WidgetTypeInitiatorFormProps) {
  const [submitCount, setSubmitCount] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [validation, setValidation] = useState(isValid(config));

  const selectedTypeId = getSelectedTypeId(config);
  const selectedOption =
    options.find((option) => option.id === selectedTypeId) ?? options[0];

  const handleNameChange = (value: string) => {
    const newConfig = setName(value, config);
    setValidation(isValid(newConfig));
    onChange(newConfig);
  };

  const handleTypeChange = (typeId: string) => {
    const newConfig = onTypeChange(typeId, config);
    setValidation(isValid(newConfig));
    onChange(newConfig);
  };

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    const nextValidation = isValid(config);
    setSubmitCount(submitCount + 1);
    setValidation(nextValidation);
    if (nextValidation[0]) {
      setIsSending(true);
      onSubmit(config).finally(() => setIsSending(false));
    }
  };

  const nameErrors = validation?.[1]?.name ?? [];
  const submitErrors = Object.values(validation?.[1] ?? {}).flat();

  return (
    <form onSubmit={handleSubmit} className="widget-initiator-form" noValidate>
      <Stack spacing={2}>
        <TextInput
          label="Nom"
          onChange={handleNameChange}
          errors={
            submitCount > 0
              ? { tag: 'Submited', errors: nameErrors }
              : { tag: 'Not submitted' }
          }
          name={'Nom'}
          value={getName(config) ?? ''}
          required
        />
        {options.length > 1 && (
          <SelectComponent
            key={selectedTypeId}
            label={typeSelectorLabel}
            initialSelection={{
              id: selectedOption.id,
              name: selectedOption.label,
            }}
            handleSelectedValues={(selected) => {
              if (selected) {
                handleTypeChange(String(selected.id));
              }
            }}
            items={options.map((option) => ({
              id: option.id,
              name: option.label,
            }))}
          />
        )}
        {submitCount > 0 && submitErrors.length > 0 && (
          <Typography color="error" variant="body2">
            {submitErrors.join('. ')}
          </Typography>
        )}
        <Box
          sx={{ m: 1, position: 'relative' }}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Button
            variant="outlined"
            loadingPosition="end"
            endIcon={<SaveIcon />}
            disabled={isSending}
            loading={isSending}
            type="submit"
          >
            Enregistrer
          </Button>
        </Box>
      </Stack>
    </form>
  );
}

export default WidgetTypeInitiatorForm;
